/**
 * When something happens (D378): the services that forward events to the organisation and the routines each may start,
 * on the Apps and routines page beside Apps and Routines. Administrators connect a service (GitHub, Azure DevOps, Linear,
 * Sentry or any service that signs what it sends), choose which shared routines it starts and when, and see what arrived
 * and what each machine answered.
 *
 * Words: "When something happens", "your organisation forwards an event", the service's own name. The other service's
 * own words (its settings page, "webhook") appear only in the steps that say where to paste the address and the secret.
 * The secret the platform makes is shown once, in the step that needs it, with a copy button.
 */

import {
  forwardedMachineWords,
  ROUTINE_EVENT_REFUSAL_WORDS,
  ROUTINE_EVENT_SERVICES,
  routineEventKindWords,
  type ForwardedEventView,
  type ForwardedMachine,
  type OutsideEventSource,
  type RoutineEventChoice,
  type WorkflowEventChoice,
  type RoutineEventRule,
  type RoutineEventSourceCreated,
  type RoutineEventSourceView,
  type RoutineEventsResponse,
} from '@aioe/shared';
import { dateTime } from '@aioe/shared/wording/dateTime';
import { BranchesRight, Bug, ListCheck, LogoGithub, PlugConnection, Pulse, Thunderbolt } from '@gravity-ui/icons';
import { Button, Modal } from '@heroui/react';
import { useState, type ReactNode } from 'react';

import { api, problemText } from '../api/client.ts';
import { timeAgo, useLoad } from '../hooks.ts';
import { ChoiceCard } from './ui/ChoiceCard.tsx';
import { ConfirmDialog, Empty, ErrorNote, inputClass, ListBlock, ListGroup, ListRow, selectClass, Stat, StatusMark, Tick, type ArtTone } from './ui.tsx';
import { RowMenu } from './ui/RowMenu.tsx';

const seg = encodeURIComponent;
const eventRoutes = {
  list: () => api<RoutineEventsResponse>('/admin/v1/routine-events'),
  create: (body: { service: OutsideEventSource; name: string; rules: RoutineEventRule[]; secret?: string }) => api<RoutineEventSourceCreated>('/admin/v1/routine-events/sources', { method: 'POST', json: body }),
  update: (id: string, body: { name?: string; enabled?: boolean; rules?: RoutineEventRule[] }) => api<RoutineEventSourceView>(`/admin/v1/routine-events/sources/${seg(id)}`, { method: 'PATCH', json: body }),
  secret: (id: string, secret?: string) => api<RoutineEventSourceCreated>(`/admin/v1/routine-events/sources/${seg(id)}/secret`, { method: 'POST', json: secret ? { secret } : {} }),
  remove: (id: string) => api<void>(`/admin/v1/routine-events/sources/${seg(id)}`, { method: 'DELETE' }),
};

const SERVICE_ICON: Record<OutsideEventSource, ReactNode> = {
  github: <LogoGithub />,
  'azure-devops': <BranchesRight />,
  linear: <ListCheck />,
  sentry: <Bug />,
  webhook: <PlugConnection />,
};
const SERVICE_BLURB: Record<OutsideEventSource, string> = {
  github: 'Workflow runs, issues, pull requests and releases.',
  'azure-devops': 'Builds, pipeline runs, pull requests and work items.',
  linear: 'Issues, comments and projects.',
  sentry: 'New issues and alerts.',
  webhook: 'Anything that can sign what it sends.',
};
const SERVICES = Object.keys(ROUTINE_EVENT_SERVICES) as OutsideEventSource[];

const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;
const listWords = (xs: string[]) => (xs.length <= 1 ? (xs[0] ?? '') : `${xs.slice(0, -1).join(', ')} and ${xs.at(-1)}`);

/** A service's state as a dot and a word. */
function standing(s: RoutineEventSourceView): { state: 'connected' | 'error' | 'disabled' | 'idle' | 'medium'; label: string } {
  if (!s.enabled) return { state: 'disabled', label: 'Off' };
  if (s.secret === 'none') return { state: 'medium', label: 'Waiting for its secret' };
  const refusedLast = s.lastRefusal && s.lastRefusal.reason !== 'off' && (!s.lastEventAt || s.lastRefusal.at > s.lastEventAt);
  if (refusedLast) return { state: 'error', label: 'Refusing what arrives' };
  return s.lastEventAt ? { state: 'connected', label: 'Receiving' } : { state: 'idle', label: 'Waiting for an event' };
}

/** What a rule starts, in a line: "Nightly triage in Shop, when a workflow run fails, from mojoup/*". */
function ruleWords(service: OutsideEventSource, r: RoutineEventSourceView['rules'][number]): string {
  const id = r.workflowId ?? r.routineId ?? '';
  const name = r.workflowId ? (r.workflowName ? `the workflow ${r.workflowName}` : id) : (r.routineName ?? id);
  const what = r.missing ? `${id} (no longer shared)` : `${name}${r.projectName ? ` in ${r.projectName}` : ''}`;
  const when = r.kinds?.length ? `when ${listWords(r.kinds.map((k) => routineEventKindWords(service, k).toLowerCase()))}` : 'on anything it sends';
  const from = r.only?.length ? `, from ${listWords(r.only)}` : '';
  return `${what}, ${when}${from}`;
}

// ── Copying ─────────────────────────────────────────────────────────

function CopyLine({ label, value, secret }: { label: string; value: string; secret?: boolean }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="grid gap-1">
      <span className="text-xs font-medium text-muted">{label}</span>
      <div className="flex items-center gap-2">
        <code className={`mono min-w-0 flex-1 select-all break-all rounded-lg bg-surface-secondary px-3 py-2 text-sm ${secret ? 'tracking-tight' : ''}`}>{value}</code>
        <Button size="sm" variant="secondary" onPress={() => void navigator.clipboard?.writeText(value).then(() => setCopied(true), () => undefined)}>
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
    </div>
  );
}

// ── The modal ───────────────────────────────────────────────────────

function Sheet({ title, blurb, children, footer, onClose }: { title: string; blurb?: ReactNode; children: ReactNode; footer: ReactNode; onClose: () => void }) {
  return (
    <Modal isOpen onOpenChange={(open) => !open && onClose()}>
      <Modal.Backdrop>
        <Modal.Container placement="center" size="lg" scroll="inside">
          <Modal.Dialog aria-label={title}>
            <Modal.CloseTrigger />
            <Modal.Header className="flex-col items-start gap-1">
              <Modal.Heading>{title}</Modal.Heading>
              {blurb ? <p className="text-sm text-muted">{blurb}</p> : null}
            </Modal.Header>
            <Modal.Body className="grid gap-4">{children}</Modal.Body>
            <Modal.Footer className="flex items-center justify-end gap-2">{footer}</Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}

/** One routine (or workflow, D401) a service starts, and when: which one, only these kinds, only from these. */
function RuleEditor({ service, rule, routines, workflows, onChange, onRemove }: { service: OutsideEventSource; rule: RoutineEventRule; routines: RoutineEventChoice[]; workflows: WorkflowEventChoice[]; onChange: (r: RoutineEventRule) => void; onRemove?: () => void }) {
  const s = ROUTINE_EVENT_SERVICES[service];
  const key = rule.workflowId ? `workflow:${rule.projectId}/${rule.workflowId}` : rule.routineId ? `routine:${rule.projectId}/${rule.routineId}` : '';
  const offered = new Set(s.kinds.map((k) => k.kind));
  const [other, setOther] = useState((rule.kinds ?? []).filter((k) => !offered.has(k)).join(', '));
  const [only, setOnly] = useState((rule.only ?? []).join(', '));
  const [cron, setCron] = useState(rule.schedule?.cron ?? '');
  const [zone, setZone] = useState(rule.schedule?.timeZone ?? '');
  const [files, setFiles] = useState((rule.githubWorkflowFiles ?? []).join(', '));
  const split = (v: string) => v.split(',').map((x) => x.trim()).filter(Boolean).slice(0, 20);
  const chosen = (rule.kinds ?? []).filter((k) => offered.has(k));
  const setKinds = (picked: string[], typed: string) => {
    const kinds = [...picked, ...split(typed)];
    onChange({ ...rule, ...(kinds.length ? { kinds } : { kinds: undefined }) });
  };
  return (
    <div className="grid gap-3 rounded-lg bg-surface-secondary p-3">
      <div className="flex items-end gap-2">
        <label className="grid min-w-0 flex-1 gap-1 text-sm">
          <span className="text-muted">Start this</span>
          <select
            className={selectClass}
            value={key}
            onChange={(e) => {
              const [what, rest] = [e.target.value.slice(0, e.target.value.indexOf(':')), e.target.value.slice(e.target.value.indexOf(':') + 1)];
              const [projectId, id] = rest.split('/');
              const { routineId: _r, workflowId: _w, ...kept } = rule;
              onChange({ ...kept, projectId: projectId ?? '', ...(what === 'workflow' ? { workflowId: id ?? '' } : { routineId: id ?? '' }) });
            }}
          >
            <option value="" disabled>
              Choose a shared routine or workflow
            </option>
            <optgroup label="Routines">
              {routines.map((r) => (
                <option key={`${r.projectId}/${r.routineId}`} value={`routine:${r.projectId}/${r.routineId}`}>
                  {r.name} · {r.projectName}
                </option>
              ))}
            </optgroup>
            {workflows.length ? (
              <optgroup label="Workflows">
                {workflows.map((w) => (
                  <option key={`${w.projectId}/${w.workflowId}`} value={`workflow:${w.projectId}/${w.workflowId}`}>
                    {w.name} · {w.projectName}
                  </option>
                ))}
              </optgroup>
            ) : null}
          </select>
        </label>
        {onRemove && (
          <Button size="sm" variant="ghost" onPress={onRemove}>
            Remove
          </Button>
        )}
      </div>
      {s.kinds.length > 0 && (
        <fieldset className="grid gap-1.5">
          <legend className="mb-1 text-sm text-muted">When (nothing ticked: on anything {s.name} sends)</legend>
          <div className="grid gap-1.5 sm:grid-cols-2">
            {s.kinds.map((k) => (
              <Tick key={k.kind} checked={chosen.includes(k.kind)} onChange={(on) => setKinds(on ? [...chosen, k.kind] : chosen.filter((x) => x !== k.kind), other)}>
                <span className="text-sm">{k.words}</span>
              </Tick>
            ))}
          </div>
        </fieldset>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="grid gap-1 text-sm">
          <span className="text-muted">{s.kinds.length ? 'Other kinds, as the service names them' : 'Only these kinds (empty: any)'}</span>
          <input
            className={inputClass}
            value={other}
            placeholder={s.kinds.length ? 'issues.closed' : 'deploy.failed'}
            onChange={(e) => {
              setOther(e.target.value);
              setKinds(chosen, e.target.value);
            }}
          />
        </label>
        <label className="grid gap-1 text-sm">
          <span className="text-muted">Only from {s.subjectWords.toLowerCase()} (empty: any)</span>
          <input
            className={inputClass}
            value={only}
            placeholder={s.subjectExample}
            onChange={(e) => {
              setOnly(e.target.value);
              const v = split(e.target.value);
              onChange({ ...rule, ...(v.length ? { only: v } : { only: undefined }) });
            }}
          />
        </label>
      </div>
      {rule.workflowId ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="grid gap-1 text-sm">
            <span className="text-muted">Also on a timetable (a cron, empty: none)</span>
            <input
              className={inputClass}
              value={cron}
              placeholder="0 9 * * 1-5"
              onChange={(e) => {
                setCron(e.target.value);
                onChange({ ...rule, schedule: e.target.value.trim() ? { cron: e.target.value.trim(), timeZone: zone.trim() || 'UTC' } : undefined });
              }}
            />
          </label>
          <label className="grid gap-1 text-sm">
            <span className="text-muted">Time zone for the timetable</span>
            <input
              className={inputClass}
              value={zone}
              placeholder="Australia/Sydney"
              onChange={(e) => {
                setZone(e.target.value);
                if (cron.trim()) onChange({ ...rule, schedule: { cron: cron.trim(), timeZone: e.target.value.trim() || 'UTC' } });
              }}
            />
          </label>
          {cron.trim() ? <p className="text-xs text-muted sm:col-span-2">Five fields: minute, hour, day of month, month, day of week. When the time comes round in that time zone the workflow starts on a machine that has it, once.</p> : null}
          {service === 'github' ? (
            <label className="grid gap-1 text-sm sm:col-span-2">
              <span className="text-muted">GitHub workflow files whose runs may join a node for this project (empty: none)</span>
              <input
                className={inputClass}
                value={files}
                placeholder=".github/workflows/agents.yml"
                onChange={(e) => {
                  setFiles(e.target.value);
                  const v = split(e.target.value);
                  onChange({ ...rule, githubWorkflowFiles: v.length ? v : undefined });
                }}
              />
              <span className="text-xs text-muted">A run of one of these files, in the repositories named above, can ask for a short-lived node with its own GitHub sign-in. Nothing is stored in GitHub. Runs started by a pull request never join.</span>
            </label>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

const blankRule = (): RoutineEventRule => ({ projectId: '' });
const cleanRules = (rules: RoutineEventRule[]) => rules.filter((r) => r.routineId || r.workflowId).map((r) => ({ projectId: r.projectId, ...(r.workflowId ? { workflowId: r.workflowId } : { routineId: r.routineId }), ...(r.only?.length ? { only: r.only } : {}), ...(r.kinds?.length ? { kinds: r.kinds } : {}), ...(r.workflowId && r.schedule ? { schedule: r.schedule } : {}), ...(r.workflowId && r.githubWorkflowFiles?.length ? { githubWorkflowFiles: r.githubWorkflowFiles } : {}) }));

/** Where to paste the address (and the secret) in the other service, and the secret itself when it is the platform's. */
function Connect({ source, secret, onPasted }: { source: RoutineEventSourceView; secret?: string; onPasted: () => void }) {
  const s = ROUTINE_EVENT_SERVICES[source.service];
  const [pasted, setPasted] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [saved, setSaved] = useState(false);
  return (
    <div className="grid gap-4">
      <ol className="grid list-decimal gap-1.5 pl-5 text-sm">
        {s.steps.map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>
      <CopyLine label="Address" value={source.url} />
      {secret && <CopyLine label="Secret (shown only now)" value={secret} secret />}
      {s.secretFromService && (
        <div className="grid gap-2">
          <label className="grid gap-1 text-sm">
            <span className="text-muted">The secret {s.name} shows</span>
            <input className={inputClass} type="password" autoComplete="off" value={pasted} onChange={(e) => setPasted(e.target.value)} placeholder={source.secret === 'pasted' ? 'A secret is kept: paste a new one to replace it' : 'Paste it here'} />
          </label>
          <div className="flex items-center gap-3">
            <Button
              size="sm"
              variant="secondary"
              isPending={busy}
              isDisabled={pasted.trim().length < 16}
              onPress={() => {
                setBusy(true);
                setError(undefined);
                eventRoutes
                  .secret(source.id, pasted.trim())
                  .then(() => (setSaved(true), setPasted(''), onPasted()))
                  .catch((e: unknown) => setError(problemText(e)))
                  .finally(() => setBusy(false));
              }}
            >
              Keep this secret
            </Button>
            {saved && <StatusMark state="connected" label="Kept, sealed" />}
          </div>
          <ErrorNote error={error} />
        </div>
      )}
    </div>
  );
}

type Dialog = { kind: 'add' } | { kind: 'edit'; source: RoutineEventSourceView } | { kind: 'connect'; source: RoutineEventSourceView; secret?: string } | { kind: 'remove'; source: RoutineEventSourceView } | { kind: 'renew'; source: RoutineEventSourceView };

function AddOrEdit({ editing, routines, workflows, onClose, onSaved }: { editing?: RoutineEventSourceView; routines: RoutineEventChoice[]; workflows: WorkflowEventChoice[]; onClose: () => void; onSaved: (made?: RoutineEventSourceCreated) => void }) {
  const [service, setService] = useState<OutsideEventSource | undefined>(editing?.service);
  const [name, setName] = useState(editing?.name ?? '');
  const [rules, setRules] = useState<RoutineEventRule[]>(editing?.rules.length ? editing.rules.map(({ projectId, routineId, workflowId, only, kinds, schedule, githubWorkflowFiles }) => ({ projectId, ...(workflowId ? { workflowId } : { routineId }), ...(only ? { only } : {}), ...(kinds ? { kinds } : {}), ...(schedule ? { schedule } : {}), ...(githubWorkflowFiles ? { githubWorkflowFiles } : {}) })) : [blankRule()]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const ready = Boolean(service && name.trim() && cleanRules(rules).length);
  const save = () => {
    if (!service) return;
    setBusy(true);
    setError(undefined);
    const body = { name: name.trim(), rules: cleanRules(rules) };
    (editing ? eventRoutes.update(editing.id, body).then(() => onSaved()) : eventRoutes.create({ service, ...body }).then((made) => onSaved(made))).catch((e: unknown) => setError(problemText(e))).finally(() => setBusy(false));
  };
  const footer = (
    <>
      <Button variant="tertiary" onPress={onClose} isDisabled={busy}>
        Cancel
      </Button>
      {service && (
        <Button variant="primary" isPending={busy} isDisabled={!ready} onPress={save}>
          {editing ? 'Save' : 'Next: connect it'}
        </Button>
      )}
    </>
  );
  if (!service)
    return (
      <Sheet title="Start routines when something happens" blurb="Choose the service that forwards events to your organisation." footer={footer} onClose={onClose}>
        <div className="grid grid-cols-[minmax(0,1fr)] gap-3 sm:grid-cols-2">
          {SERVICES.map((id, i) => (
            // Five in two columns: the last spans the row, so no card stands alone.
            <div key={id} className={`grid ${i === SERVICES.length - 1 && SERVICES.length % 2 ? 'sm:col-span-2' : ''}`}>
              <ChoiceCard testId={id} selected={false} onPress={() => (setService(id), setName(id === 'webhook' ? '' : ROUTINE_EVENT_SERVICES[id].name))} icon={SERVICE_ICON[id]} title={ROUTINE_EVENT_SERVICES[id].name} description={SERVICE_BLURB[id]} />
            </div>
          ))}
        </div>
      </Sheet>
    );
  const s = ROUTINE_EVENT_SERVICES[service];
  return (
    <Sheet title={editing ? `What ${editing.name} starts` : `Forward events from ${s.name}`} blurb="Which shared routines it may start, and when. A machine that has the routine runs it; the rest say so." footer={footer} onClose={onClose}>
      <label className="grid gap-1 text-sm">
        <span className="text-muted">Name</span>
        <input className={inputClass} value={name} maxLength={80} placeholder={service === 'webhook' ? 'Deploys' : `${s.name} for the shop`} onChange={(e) => setName(e.target.value)} autoFocus={!editing} />
      </label>
      {routines.length === 0 ? (
        <p className="text-sm text-muted">No routine is shared in a project yet. When someone publishes one from their workbench to a project, it can be chosen here.</p>
      ) : (
        <div className="grid gap-3">
          {rules.map((r, i) => (
            <RuleEditor key={i} service={service} rule={r} routines={routines} workflows={workflows} onChange={(next) => setRules(rules.map((x, j) => (j === i ? next : x)))} {...(rules.length > 1 ? { onRemove: () => setRules(rules.filter((_, j) => j !== i)) } : {})} />
          ))}
          {rules.length < 20 && (
            <div>
              <Button size="sm" variant="ghost" onPress={() => setRules([...rules, blankRule()])}>
                Add another routine
              </Button>
            </div>
          )}
        </div>
      )}
      <ErrorNote error={error} />
    </Sheet>
  );
}

// ── The log ─────────────────────────────────────────────────────────

const MACHINE_MARK: Record<ForwardedMachine['state'], 'connected' | 'working' | 'disabled' | 'error' | 'offline'> = { done: 'connected', pending: 'working', refused: 'disabled', failed: 'error', expired: 'offline' };

function eventStanding(e: ForwardedEventView): { state: 'connected' | 'working' | 'idle' | 'disabled' | 'error'; label: string } {
  const machines = e.starts.flatMap((s) => s.machines);
  const done = machines.filter((m) => m.state === 'done').length;
  if (e.outcome === 'no_match') return { state: 'idle', label: 'Nothing to start' };
  if (e.outcome === 'no_machine') return { state: 'disabled', label: 'Not sent' };
  if (done) return { state: 'connected', label: `Started on ${plural(done, 'machine')}` };
  if (machines.some((m) => m.state === 'pending')) return { state: 'working', label: 'Waiting for machines' };
  if (machines.some((m) => m.state === 'failed')) return { state: 'error', label: 'Failed' };
  return { state: 'disabled', label: 'Not started' };
}

function EventRow({ e }: { e: ForwardedEventView }) {
  const [open, setOpen] = useState(false);
  const st = eventStanding(e);
  const routines = e.starts.map((s) => s.routineName);
  return (
    <ListRow
      icon={SERVICE_ICON[e.service]}
      title={
        e.event.url ? (
          <a className="hover:underline" href={e.event.url} target="_blank" rel="noreferrer">
            {e.event.summary}
          </a>
        ) : (
          e.event.summary
        )
      }
      description={
        <>
          {[`${ROUTINE_EVENT_SERVICES[e.service].name}, through ${e.sourceName}`, routines.length ? `for ${listWords(routines)}` : ''].filter(Boolean).join(' · ')}
          {' · '}
          <time dateTime={e.receivedAt} title={dateTime(e.receivedAt)}>
            {timeAgo(e.receivedAt)}
          </time>
        </>
      }
      status={<StatusMark state={st.state} label={st.label} />}
      compactControl
      control={
        <>
          {e.starts.length > 0 && (
            <Button size="sm" variant="ghost" className="text-muted" aria-expanded={open} onPress={() => setOpen(!open)}>
              {open ? 'Hide' : 'Machines'}
            </Button>
          )}
        </>
      }
    >
      {open && (
        <div className="grid gap-3 rounded-lg bg-surface-secondary px-3 py-2 text-sm">
          {e.starts.map((s) => (
            <div key={`${s.projectId}/${s.workflowId ?? s.routineId}`} className="grid gap-1">
              <div className="font-medium">{s.routineName}</div>
              {s.note && <p className="text-xs text-muted">{s.note}</p>}
              <ul className="grid gap-1">
                {s.machines.map((m) => (
                  <li key={m.orderId} className="flex items-baseline gap-3">
                    <span className="min-w-0 flex-1">
                      <span>{m.name}</span>
                      <span className="text-xs text-muted"> · {forwardedMachineWords(m)}</span>
                    </span>
                    <StatusMark state={MACHINE_MARK[m.state]} label={m.state === 'done' ? 'Started' : m.state === 'pending' ? 'Waiting' : m.state === 'expired' ? 'No answer' : m.code === 'no_routine' || m.code === 'no_workflow' ? 'Not here' : m.state === 'failed' ? 'Failed' : 'Turned down'} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </ListRow>
  );
}

// ── The whole area ──────────────────────────────────────────────────

export function WhenSomethingHappens({ tone }: { tone: ArtTone }) {
  const data = useLoad(() => eventRoutes.list(), [], 30_000);
  const [dialog, setDialog] = useState<Dialog>();
  const [error, setError] = useState<string>();
  const sources = data.data?.sources ?? [];
  const events = data.data?.events ?? [];
  const routines = data.data?.routines ?? [];
  const workflows = data.data?.workflows ?? [];
  const waiting = sources.filter((s) => s.enabled && (s.secret === 'none' || standing(s).state === 'error'));
  const started = events.reduce((n, e) => n + e.starts.reduce((k, s) => k + s.machines.filter((m) => m.state === 'done').length, 0), 0);
  const run = (f: () => Promise<unknown>) => {
    setError(undefined);
    f().then(
      () => data.reload(),
      (e: unknown) => setError(problemText(e)),
    );
  };
  const act = (s: RoutineEventSourceView, key: string) => {
    if (key === 'toggle') run(() => eventRoutes.update(s.id, { enabled: !s.enabled }));
    else if (key === 'edit') setDialog({ kind: 'edit', source: s });
    else if (key === 'connect') setDialog({ kind: 'connect', source: s });
    else if (key === 'renew') setDialog({ kind: 'renew', source: s });
    else if (key === 'remove') setDialog({ kind: 'remove', source: s });
  };
  const close = () => setDialog(undefined);

  return (
    <>
      <ErrorNote error={data.error ?? error} />
      <div className="art-stats">
        <Stat tone={tone} icon={<Thunderbolt />} label="Services" value={data.data ? sources.filter((s) => s.enabled).length : undefined} detail={data.data ? `${plural(sources.length, 'connected service')} in all` : ''} />
        <Stat tone={tone} icon={<Thunderbolt />} label="Need attention" value={data.data ? waiting.length : undefined} detail={waiting.length ? `${waiting[0]!.name}: ${standing(waiting[0]!).label.toLowerCase()}` : 'All receiving'} needsYou={waiting.length > 0} />
        <Stat tone={tone} icon={<Pulse />} label="Routines started" value={data.data ? started : undefined} detail={`From the last ${plural(events.length, 'event')}`} />
      </div>

      <ListGroup
        title="Connected services"
        count={data.data ? sources.length : undefined}
        description="Your organisation forwards an event from a service you connect to the machines that have the routine it starts. Each machine's policy and the routine's own limit still decide whether it runs."
        actions={
          sources.length ? (
            <Button size="sm" variant="secondary" onPress={() => setDialog({ kind: 'add' })}>
              Connect a service
            </Button>
          ) : undefined
        }
        empty={
          data.data && sources.length === 0 ? (
            <Empty
              tone={tone}
              icon={<Thunderbolt />}
              title="Nothing forwarded yet"
              body="Connect GitHub, Azure DevOps, Linear, Sentry or another service, and a shared routine starts when a pipeline fails, an issue is labelled or an alert fires."
              action={
                <Button size="sm" variant="primary" onPress={() => setDialog({ kind: 'add' })}>
                  Connect a service
                </Button>
              }
            />
          ) : undefined
        }
      >
        {!data.data ? <ListBlock className="text-muted">Reading the connected services…</ListBlock> : null}
        {sources.map((s) => {
          const st = standing(s);
          const refused = s.lastRefusal && st.state === 'error' ? `The last one was refused: ${ROUTINE_EVENT_REFUSAL_WORDS[s.lastRefusal.reason]}` : s.lastEventAt ? `Last event ${timeAgo(s.lastEventAt)}` : `Connected ${timeAgo(s.createdAt)}${s.createdBy.name ? ` by ${s.createdBy.name}` : ''}`;
          return (
            <ListRow
              key={s.id}
              icon={SERVICE_ICON[s.service]}
              title={s.name !== ROUTINE_EVENT_SERVICES[s.service].name ? `${s.name} · ${ROUTINE_EVENT_SERVICES[s.service].name}` : s.name}
              description={
                <>
                  {s.rules.length ? (
                    s.rules.map((r) => (
                      <span key={`${r.projectId}/${r.workflowId ?? r.routineId}`} className="block">
                        Starts {ruleWords(s.service, r)}
                      </span>
                    ))
                  ) : (
                    <span className="block">Starts nothing yet</span>
                  )}
                  <span className="block">{refused}</span>
                </>
              }
              status={<StatusMark state={st.state} label={st.label} />}
              compactControl
              control={
                <>
                  <RowMenu
                    label={`More for ${s.name}`}
                    items={[
                      { id: 'edit', label: 'What it starts…' },
                      { id: 'connect', label: ROUTINE_EVENT_SERVICES[s.service].secretFromService ? 'Address and secret…' : 'Address and steps…' },
                      !ROUTINE_EVENT_SERVICES[s.service].secretFromService && { id: 'renew', label: 'Make a new secret…' },
                      { id: 'toggle', label: s.enabled ? 'Turn off' : 'Turn on' },
                      { id: 'remove', label: 'Remove…', danger: true },
                    ]}
                    onAction={(k) => act(s, k)}
                  />
                </>
              }
            />
          );
        })}
      </ListGroup>

      <ListGroup title="What happened" count={data.data ? events.length : undefined} description="The newest events your organisation forwarded, what each started and what every machine answered. The last 500 of each service are kept for 30 days.">
        {events.length === 0 ? <ListBlock className="text-muted">Nothing has arrived yet. Once a connected service sends something, it is listed here with the routines it started.</ListBlock> : events.map((e) => <EventRow key={e.id} e={e} />)}
      </ListGroup>

      {(dialog?.kind === 'add' || dialog?.kind === 'edit') && (
        <AddOrEdit
          {...(dialog.kind === 'edit' ? { editing: dialog.source } : {})}
          routines={routines}
          workflows={workflows}
          onClose={close}
          onSaved={(made) => {
            data.reload();
            if (made) setDialog({ kind: 'connect', source: made.source, ...(made.secret ? { secret: made.secret } : {}) });
            else close();
          }}
        />
      )}
      {dialog?.kind === 'connect' && (
        <Sheet
          title={`Connect ${ROUTINE_EVENT_SERVICES[dialog.source.service].name}`}
          blurb={dialog.secret ? 'Paste the address and the secret into the service now: the secret is not shown again.' : ROUTINE_EVENT_SERVICES[dialog.source.service].secretFromService ? 'Paste the address into the service, then paste back the secret it makes.' : 'The secret was shown once, when it was made. Make a new one from the menu if it is lost.'}
          onClose={() => (close(), data.reload())}
          footer={
            <Button variant="primary" onPress={() => (close(), data.reload())}>
              Done
            </Button>
          }
        >
          <Connect source={dialog.source} {...(dialog.secret ? { secret: dialog.secret } : {})} onPasted={data.reload} />
        </Sheet>
      )}
      <ConfirmDialog
        isOpen={dialog?.kind === 'renew'}
        title="Make a new secret?"
        confirmLabel="Make a new secret"
        onCancel={close}
        onConfirm={async () => {
          if (dialog?.kind !== 'renew') return;
          const s = dialog.source;
          try {
            const made = await eventRoutes.secret(s.id);
            data.reload();
            setDialog({ kind: 'connect', source: made.source, ...(made.secret ? { secret: made.secret } : {}) });
          } catch (e) {
            setError(problemText(e));
            close();
          }
        }}
      >
        <p>The old secret stops working at once: what {dialog?.kind === 'renew' ? ROUTINE_EVENT_SERVICES[dialog.source.service].name : 'the service'} sends is refused until the new one is pasted there.</p>
      </ConfirmDialog>
      <ConfirmDialog
        isOpen={dialog?.kind === 'remove'}
        title={`Remove ${dialog?.kind === 'remove' ? dialog.source.name : ''}?`}
        confirmLabel="Remove"
        danger
        onCancel={close}
        onConfirm={async () => {
          if (dialog?.kind === 'remove') await eventRoutes.remove(dialog.source.id).then(() => data.reload(), (e: unknown) => setError(problemText(e)));
          close();
        }}
      >
        <p>Its address stops listening and what it forwarded leaves the list. Routines already started are not stopped.</p>
      </ConfirmDialog>
    </>
  );
}
